<!-- eslint-disable max-lines -->
<script setup lang="ts" generic="V extends string | number = string | number">
/* eslint-disable max-lines */
// Legacy multi-choice behavior on Headless Combobox plus the shared floating adapter.
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue'
import { computed, ref, useId } from 'vue'

import { injectUiLabels } from '@/shared/i18n'
import { useComboboxClickOpen, useViewportRestore } from '@/shared/ui/composables'
import { useFloating } from '@/shared/ui/floating'
import { Icon } from '@/shared/ui/icon'
import { OptionVisual } from '@/shared/ui/option-visual'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'
import type { SelectOption } from '@/shared/ui/select'
import type { UiSize, UiAsyncState } from '@/shared/ui/types'

const model = defineModel<V[]>({ default: () => [] })
const props = withDefaults(
  defineProps<{
    options: SelectOption<V>[]
    label?: string
    hideLabel?: boolean
    placeholder?: string
    size?: UiSize
    searchable?: boolean
    removeLabel?: string
    emptyLabel?: string
    disabled?: boolean
    state?: UiAsyncState
    max?: number
    /** Render the selection as a compact comma-separated summary instead of removable chips. */
    displayChips?: boolean
    /** Collapse selections after the first N into a non-removable `+K` summary chip. */
    maxVisibleChips?: number
    /** `picker` moves search and a "select all" row into the floating panel itself (Select.vue's
     *  own picker recipe) instead of the trigger — for a full-height field picker over a compact
     *  toolbar control. */
    variant?: 'default' | 'picker'
    /** `picker` only: offer a row that selects/clears every currently visible (filtered) option. */
    showSelectAll?: boolean
    /** `picker` only: caller-localised search placeholder; fallback resolves `ui.search`. */
    searchPlaceholder?: string
    /** `picker` only: caller-localised select-all row copy; fallback resolves `ui.selectAll`. */
    selectAllLabel?: string
    /** `picker` only: explicit floating panel width (defaults to content-driven sizing). */
    panelWidth?: string
  }>(),
  {
    size: 'md',
    searchable: false,
    disabled: false,
    state: 'ready',
    displayChips: true,
    variant: 'default',
    showSelectAll: false,
  },
)

const emit = defineEmits<{ change: [value: V[]] }>()
const labelId = `ui-ms-label-${useId()}`

// Resolve reserved labels through the injected `ui.*` bag without importing vue-i18n.
const uiLabels = injectUiLabels()
const resolvedRemoveLabel = computed(() => props.removeLabel ?? uiLabels.remove)
const resolvedEmptyLabel = computed(() => props.emptyLabel ?? uiLabels.noResults)
const resolvedSearchPlaceholder = computed(() => props.searchPlaceholder ?? uiLabels.search)
const resolvedSelectAllLabel = computed(() => props.selectAllLabel ?? uiLabels.selectAll)

const query = ref('')
const selectedOptions = computed(() => props.options.filter((o) => model.value.includes(o.value)))

// Legacy parity (v3 OrphexCustomMultiSelect's `orderOptionsBySelection`): checked options float to
// the top of the dropdown so a picker with a long option set (e.g. report columns) never buries them.
function orderBySelection(list: SelectOption<V>[]): SelectOption<V>[] {
  if (!model.value.length) return list
  return [...list].sort((left, right) => {
    const leftSelected = model.value.includes(left.value) ? 1 : 0
    const rightSelected = model.value.includes(right.value) ? 1 : 0
    return rightSelected - leftSelected
  })
}
const visibleChips = computed(() =>
  props.maxVisibleChips === undefined
    ? selectedOptions.value
    : selectedOptions.value.slice(0, Math.max(0, props.maxVisibleChips)),
)
const hiddenChipCount = computed(() => selectedOptions.value.length - visibleChips.value.length)
const summaryChipLabel = computed(() => `+${hiddenChipCount.value}`)
const atMax = computed(() => props.max !== undefined && model.value.length >= props.max)
const filtered = computed(() => {
  if (!props.searchable || query.value === '') return orderBySelection(props.options)
  const q = query.value.toLowerCase()
  return orderBySelection(props.options.filter((o) => o.label.toLowerCase().includes(q)))
})

const reference = ref<HTMLElement | null>(null)
const floatingEl = ref<HTMLElement | null>(null)
const { floatingStyles } = useFloating(reference, floatingEl, {
  placement: 'bottom-start',
  offset: 4,
  strategy: 'fixed',
})
const { preserveViewportAfterFocus } = useViewportRestore()

// Headless UI's multi-select mode never auto-closes on pick. A `picker` capped at one choice reads
// as a plain select despite its checkbox, so it closes the same way its own Escape handling does.
function closeIfSingleChoice(): void {
  if (props.variant !== 'picker' || props.max !== 1) return
  const input = floatingEl.value?.querySelector<HTMLElement>('input')
  input?.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }),
  )
}

function onChange(values: V[]): void {
  let next = values
  if (props.max !== undefined && next.length > props.max) {
    // `default` rejects the change, leaving every other option disabled until a slot frees up.
    // `picker` reads as "pick up to N", so it swaps out the oldest instead (see the `disabled`
    // skip below, which is what keeps a `picker` option clickable past the cap at all).
    if (props.variant !== 'picker') return
    const added = next.filter((value) => !model.value.includes(value))
    const kept = next.filter((value) => !added.includes(value))
    next = [...kept, ...added].slice(-props.max)
  }
  model.value = next
  emit('change', next)
  preserveViewportAfterFocus(() => {
    // `picker`'s field lives in the teleported panel, not the trigger — whose two `ComboboxButton`s
    // (field + chevron) fight over Headless UI's one shared `buttonRef`. Focusing the loser reads
    // as focus leaving the combobox and closes it on every pick, so `picker` only ever refocuses
    // the panel's own input — the one element Headless UI still recognizes as "inside".
    const scope = props.variant === 'picker' ? floatingEl.value : reference.value
    const selector = props.variant === 'picker' ? 'input' : 'input, button:not([disabled])'
    scope?.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true })
  })
  closeIfSingleChoice()
}

function remove(value: V): void {
  onChange(model.value.filter((v) => v !== value))
}

// "Select all" promises every option ends up checked — a role capped below the option count can
// never deliver that, so the row is withheld rather than left to silently cap out part-way.
const canOfferSelectAll = computed(
  () => props.max === undefined || props.max >= props.options.filter((o) => !o.disabled).length,
)

// Every currently visible (filtered), selectable option is already checked — the row's own
// checkbox reads this to show its own indeterminate-free checked state.
const allFilteredSelected = computed(() => {
  const selectable = filtered.value.filter((o) => !o.disabled)
  return selectable.length > 0 && selectable.every((o) => model.value.includes(o.value))
})

function toggleSelectAll(): void {
  const selectable = filtered.value.filter((o) => !o.disabled).map((o) => o.value)
  onChange(
    allFilteredSelected.value
      ? model.value.filter((value) => !selectable.includes(value))
      : [...new Set([...model.value, ...selectable])],
  )
}

// `picker` moves the searchable field into the panel (see template), so the trigger itself is
// always a plain button there — this glue (and the trigger's own `ComboboxInput`) is `default`-only.
const triggerSearchable = computed(() => props.variant === 'default' && props.searchable)
const onControlClick = useComboboxClickOpen(
  reference,
  '.ui-ms__chevron',
  () => triggerSearchable.value,
)
</script>

<template>
  <Combobox
    v-slot="{ open }"
    :model-value="model"
    :disabled="disabled"
    multiple
    as="div"
    class="ui-ms relative w-full font-sans"
    :class="`ui-ms--${size}`"
    @update:model-value="(v: V[]) => onChange(v)"
  >
    <label
      v-if="label"
      :id="labelId"
      class="ui-ms__label absolute -top-2 left-xs z-raised rounded-sm bg-[linear-gradient(to_bottom,transparent_40%,var(--ui-color-surface)_50%)] px-2xs text-xs font-medium text-muted"
      :class="{ 'ui-ms__label--sr-only sr-only': hideLabel }"
    >
      {{ label }}
    </label>

    <div
      ref="reference"
      class="ui-ms__control relative flex flex-wrap items-center gap-[0.3rem] rounded-sm border border-border bg-surface py-2xs pr-8 pl-[0.4rem] shadow-input focus-within:border-ring focus-within:shadow-[0_0_0_1px_var(--ui-color-ring)]"
      :class="[
        size === 'xs' || size === 'sm' ? 'min-h-8' : '',
        size === 'md' ? 'min-h-19' : '',
        size === 'lg' || size === 'xl' ? 'min-h-12' : '',
        { 'is-disabled cursor-not-allowed opacity-60': disabled },
      ]"
      v-bind="triggerSearchable ? { onClickCapture: onControlClick } : {}"
    >
      <span
        v-for="opt in displayChips ? visibleChips : []"
        :key="String(opt.value)"
        class="ui-ms__chip inline-flex max-w-full items-center gap-[0.3rem] rounded-full border border-primary-subtle bg-surface-sunken py-[0.1rem] pr-0.5 pl-xs text-xs leading-none text-primary"
      >
        <slot name="chip" :option="opt">
          <OptionVisual :visual="opt.meta?.visual" :label="opt.label" :size="14" />
          <span
            class="ui-ms__chip-label min-w-0 max-w-48 overflow-hidden text-ellipsis whitespace-nowrap"
            :title="opt.label"
            >{{ opt.label }}</span
          >
        </slot>
        <button
          v-if="!disabled"
          type="button"
          class="ui-ms__chip-x inline-flex border-0 bg-transparent text-[0.7rem] text-muted hover:text-danger"
          :aria-label="resolvedRemoveLabel"
          @click.stop="remove(opt.value)"
        >
          ✕
        </button>
      </span>
      <span
        v-if="displayChips && hiddenChipCount > 0"
        class="ui-ms__chip ui-ms__chip--summary inline-flex max-w-full items-center gap-[0.3rem] rounded-full border border-primary-subtle bg-surface-sunken py-[0.1rem] pr-0.5 pl-xs text-xs font-semibold leading-none text-muted"
      >
        {{ summaryChipLabel }}
      </span>
      <span
        v-if="!displayChips && selectedOptions.length"
        class="ui-ms__selection-summary min-w-0 shrink grow-3 basis-0 overflow-hidden text-ellipsis whitespace-nowrap text-[0.8125rem] text-fg"
        :title="selectedOptions.map((option) => option.label).join(', ')"
      >
        {{ selectedOptions.map((option) => option.label).join(', ') }}
      </span>

      <ComboboxInput
        v-if="triggerSearchable"
        class="ui-ms__field min-w-16 flex-1 border-0 bg-transparent text-left text-[0.8125rem] text-fg outline-none"
        :aria-labelledby="label ? labelId : undefined"
        :placeholder="selectedOptions.length ? '' : placeholder"
        @change="query = ($event.target as HTMLInputElement).value"
      />
      <ComboboxButton
        v-else
        class="ui-ms__field ui-ms__field--button absolute inset-0 w-full min-w-0 cursor-pointer border-0 bg-transparent py-0 pr-8 pl-[0.4rem] text-left text-[0.8125rem] text-transparent outline-none"
        :aria-label="
          selectedOptions.length
            ? selectedOptions.map((option) => option.label).join(', ')
            : placeholder
        "
      >
        <span v-if="!selectedOptions.length" class="ui-ms__placeholder text-muted">{{
          placeholder
        }}</span>
      </ComboboxButton>

      <ComboboxButton
        class="ui-ms__chevron pointer-events-none absolute right-xs top-[0.6rem] border-0 bg-transparent text-xs text-muted"
        aria-hidden="true"
      >
        ▾
      </ComboboxButton>
    </div>

    <OverlayPortal>
      <FloatingLayer
        v-if="open"
        v-model:element="floatingEl"
        kind="dropdown"
        class="ui-ms__floating"
        :class="
          variant === 'picker'
            ? 'w-max max-w-[min(22rem,var(--ui-floating-available-width,calc(100vw_-_0.75rem)))]'
            : 'w-[min(var(--ui-floating-reference-width,12rem),var(--ui-floating-available-width,calc(100vw_-_0.75rem)))]'
        "
        :style="floatingStyles"
      >
        <div
          class="ui-ms__panel"
          :class="
            variant === 'picker'
              ? 'overflow-hidden rounded-lg border border-border bg-surface shadow-card'
              : 'min-w-48'
          "
          :style="{ width: panelWidth }"
        >
          <div
            v-if="variant === 'picker' && searchable"
            class="ui-ms__picker-search relative m-2 flex h-9 items-center rounded-sm border border-border bg-surface text-muted focus-within:border-[color:var(--ui-color-ring)] focus-within:shadow-[0_0_0_1px_var(--ui-color-ring)]"
          >
            <ComboboxInput
              class="ui-ms__picker-search-field h-full w-full bg-transparent px-3 text-sm outline-none placeholder:text-sm [font:inherit]"
              :display-value="() => ''"
              :placeholder="resolvedSearchPlaceholder"
              @change="query = ($event.target as HTMLInputElement).value"
            />
          </div>
          <button
            v-if="variant === 'picker' && showSelectAll && canOfferSelectAll"
            type="button"
            class="ui-ms__select-all-button flex min-h-9 w-full cursor-pointer items-center gap-sm px-md py-xs text-left text-xs font-bold text-fg hover:bg-surface-sunken"
            @click="toggleSelectAll"
          >
            <span
              class="ui-ms__box flex h-5 w-5 flex-none items-center justify-center rounded-sm border"
              :class="
                allFilteredSelected
                  ? 'is-checked border-info bg-info text-primary-fg'
                  : 'border-border'
              "
              aria-hidden="true"
            >
              <Icon v-if="allFilteredSelected" name="check" :size="12" />
            </span>
            {{ resolvedSelectAllLabel }}
          </button>
          <ComboboxOptions
            class="ui-ms__options m-0 max-h-64 w-full list-none overflow-y-auto text-fg"
            :class="
              variant === 'picker'
                ? 'rounded-none border-0 p-0 pb-xs shadow-none'
                : 'rounded-sm border border-border bg-surface p-2xs shadow-pop'
            "
          >
            <div
              v-if="state === 'loading'"
              class="ui-ms__msg px-sm py-xs text-[0.8125rem] text-muted"
            >
              <slot name="loading">…</slot>
            </div>
            <div
              v-else-if="state === 'error'"
              class="ui-ms__msg ui-ms__msg--error px-sm py-xs text-[0.8125rem] text-danger"
            >
              <slot name="error">!</slot>
            </div>
            <template v-else>
              <div
                v-if="filtered.length === 0"
                class="ui-ms__msg px-sm py-xs text-[0.8125rem] text-muted"
              >
                <slot name="empty">{{ resolvedEmptyLabel }}</slot>
              </div>
              <ComboboxOption
                v-for="opt in filtered"
                :key="String(opt.value)"
                v-slot="{ active, selected }"
                :value="opt.value"
                :disabled="
                  opt.disabled ||
                  (atMax && (variant !== 'picker' || max === 0) && !model.includes(opt.value))
                "
                as="template"
              >
                <li
                  class="ui-ms__option flex cursor-pointer items-center"
                  :class="[
                    // Headless UI activates the SELECTED option as the panel opens, so painting
                    // `active` would tint that row grey the moment a picker opens — the resting
                    // state the design shows plain. Picker keeps the tint on real hover instead;
                    // the blue label plus checked box already mark the selection itself.
                    variant === 'picker'
                      ? 'min-h-9 gap-sm rounded-none px-md py-xs text-xs hover:bg-surface-sunken'
                      : 'min-h-9 gap-xs rounded-sm px-[0.6rem] py-[0.4rem] text-[0.8125rem]',
                    { 'is-active': active, 'bg-surface-sunken': active && variant !== 'picker' },
                    selected
                      ? variant === 'picker'
                        ? 'is-selected text-info'
                        : 'is-selected text-primary'
                      : '',
                  ]"
                >
                  <slot name="option" :option="opt" :active="active" :selected="selected">
                    <span
                      class="ui-ms__box flex flex-none items-center justify-center border"
                      :class="[
                        variant === 'picker' ? 'h-5 w-5 rounded-sm' : 'h-4 w-4 rounded-sm',
                        selected
                          ? variant === 'picker'
                            ? 'is-checked border-info bg-info text-primary-fg'
                            : 'is-checked border-primary bg-primary text-primary-fg'
                          : 'border-border',
                      ]"
                      aria-hidden="true"
                    >
                      <Icon v-if="selected" name="check" :size="variant === 'picker' ? 12 : 10" />
                    </span>
                    <OptionVisual :visual="opt.meta?.visual" :label="opt.label" :size="16" />
                    <span class="ui-ms__option-label min-w-0 break-words">{{ opt.label }}</span>
                  </slot>
                </li>
              </ComboboxOption>
            </template>
          </ComboboxOptions>
        </div>
      </FloatingLayer>
    </OverlayPortal>
  </Combobox>
</template>
